// Browser stand-in for node:fs.
//
// Async operations delegate to the fs-promises shim, which consults the
// File System Access mount engine (fsa-core.cjs) before falling back to
// the baseline no-filesystem policy: reads fail ENOENT, listings come
// back empty, writes pretend success. Sync variants remain unsupported —
// the FSA API is async-only and the renderer's critical paths don't need
// them.
const { EventEmitter } = require("events");
const { Buffer } = require("buffer");

const core = require("./fsa-core.cjs");
const fsPromises = require("./fs-promises.cjs");

function enoent(filePath) {
    const err = new Error(
        `ENOENT: no such file or directory, web build has no filesystem '${String(filePath)}'`,
    );
    err.code = "ENOENT";
    err.errno = -2;
    err.path = String(filePath);
    return err;
}

function lastCallback(args) {
    for (let i = args.length - 1; i >= 0; i--) {
        if (typeof args[i] === "function") {
            return args[i];
        }
    }
    return undefined;
}

function toU8(d) {
    if (d instanceof Uint8Array) return d;
    if (Buffer.isBuffer(d)) {
        return new Uint8Array(d.buffer, d.byteOffset, d.byteLength);
    }
    if (typeof d === "string") return new TextEncoder().encode(d);
    if (d && d.buffer instanceof ArrayBuffer) {
        return new Uint8Array(d.buffer, d.byteOffset ?? 0, d.byteLength);
    }
    return new Uint8Array();
}

function badf(fd) {
    const err = new Error(`EBADF: bad file descriptor, fd ${fd}`);
    err.code = "EBADF";
    err.errno = -9;
    return err;
}

// ---------------------------------------------------------------------------
// file-descriptor table: util/fs.ts wraps fs.open/write/read/close/fsync and
// the atomic-write helper (fsAtomic.writeFileAtomic) writes via fd, so we need
// real fd semantics. Content is buffered per fd and persisted to the fs core
// (overlay / OPFS / game mount) on every write so a subsequent path read sees
// it. Without this, writeFileAtomic always fails its checksum check (empty
// temp file) and `fs.write`/`fs.read` being undefined throws
// "func is not a function".
// ---------------------------------------------------------------------------
let nextFd = 3;
const fdTable = new Map();

function fdOpen(filePath, flags, ...rest) {
    const cb = lastCallback(rest);
    const flagStr = typeof flags === "string" ? flags : "r";
    const run = async () => {
        let content = new Uint8Array();
        if (flagStr.startsWith("r") || flagStr.includes("a")) {
            const existing = await fsPromises
                .readFile(filePath)
                .catch(() => null);
            if (existing != null) content = toU8(existing);
        }
        const fd = nextFd++;
        fdTable.set(fd, {
            path: filePath,
            content,
            pos: flagStr.includes("a") ? content.length : 0,
        });
        return fd;
    };
    if (cb !== undefined) {
        run().then(
            (fd) => cb(null, fd),
            (err) => cb(err),
        );
        return undefined;
    }
    return run();
}

function fdWrite(fd, data, ...rest) {
    const cb = lastCallback(rest);
    const run = async () => {
        const entry = fdTable.get(fd);
        if (entry === undefined) throw badf(fd);
        let bytes;
        let position = null;
        if (typeof data === "string") {
            bytes = new TextEncoder().encode(data);
            if (typeof rest[0] === "number") position = rest[0];
        } else {
            const buf = toU8(data);
            const offset = typeof rest[0] === "number" ? rest[0] : 0;
            const length =
                typeof rest[1] === "number" ? rest[1] : buf.length - offset;
            position = typeof rest[2] === "number" ? rest[2] : null;
            bytes = buf.subarray(offset, offset + length);
        }
        const at = position == null ? entry.pos : position;
        const end = at + bytes.length;
        if (end > entry.content.length) {
            const grown = new Uint8Array(end);
            grown.set(entry.content);
            entry.content = grown;
        }
        entry.content.set(bytes, at);
        if (position == null) entry.pos = end;
        await core.writeFile(entry.path, entry.content);
        return bytes.length;
    };
    if (cb !== undefined) {
        run().then(
            (n) => cb(null, n, data),
            (err) => cb(err),
        );
        return undefined;
    }
    return run().then((n) => ({ bytesWritten: n, buffer: data }));
}

function fdRead(fd, buffer, ...rest) {
    const cb = lastCallback(rest);
    const run = async () => {
        const entry = fdTable.get(fd);
        if (entry === undefined) throw badf(fd);
        const target = toU8(buffer);
        const offset = typeof rest[0] === "number" ? rest[0] : 0;
        const length =
            typeof rest[1] === "number" ? rest[1] : target.length - offset;
        const position = typeof rest[2] === "number" ? rest[2] : null;
        const at = position == null ? entry.pos : position;
        const n = Math.max(0, Math.min(length, entry.content.length - at));
        target.set(entry.content.subarray(at, at + n), offset);
        if (position == null) entry.pos = at + n;
        return n;
    };
    if (cb !== undefined) {
        run().then(
            (n) => cb(null, n, buffer),
            (err) => cb(err),
        );
        return undefined;
    }
    return run().then((n) => ({ bytesRead: n, buffer }));
}

// callback/promise dual-mode wrapper around an fs-promises op
function delegate(name) {
    return function (...args) {
        const cb = lastCallback(args);
        const opArgs =
            cb !== undefined ? args.slice(0, args.indexOf(cb)) : args;
        const result = fsPromises[name](...opArgs);
        if (cb !== undefined) {
            result.then(
                (value) => cb(null, value),
                (err) => cb(err),
            );
            return undefined;
        }
        return result;
    };
}

class WriteStreamShim extends EventEmitter {
    write(_chunk, _enc, cb) {
        if (typeof _enc === "function") cb = _enc;
        if (cb) setTimeout(cb, 0);
        return true;
    }
    end(cb) {
        if (typeof cb === "function") setTimeout(cb, 0);
        setTimeout(() => {
            this.emit("finish");
            this.emit("close");
        }, 0);
    }
    close() {}
}

// Reads the whole file through the fs core (file mounts / OPFS / dir mounts)
// and emits it as a single chunk. Enough for the renderer's stream consumers
// (md5 hashing, etc.), which only listen for data/end/error.
class ReadStreamShim extends EventEmitter {
    constructor(filePath) {
        super();
        this.path = filePath;
        setTimeout(() => {
            fsPromises.readFile(filePath).then(
                (data) => {
                    const buf = Buffer.isBuffer(data)
                        ? data
                        : Buffer.from(data);
                    if (buf.length > 0) {
                        this.emit("data", buf);
                    }
                    this.emit("end");
                    this.emit("close");
                },
                (err) => this.emit("error", err),
            );
        }, 0);
    }
    pipe(dest) {
        this.on("data", (chunk) => dest.write?.(chunk));
        this.on("end", () => dest.end?.());
        return dest;
    }
    close() {}
    destroy() {}
}

const constants = {
    F_OK: 0,
    R_OK: 4,
    W_OK: 2,
    X_OK: 1,
    O_RDONLY: 0,
    O_WRONLY: 1,
    O_RDWR: 2,
    O_CREAT: 64,
    O_EXCL: 128,
    O_TRUNC: 512,
    O_APPEND: 1024,
};

const fs = {
    constants,

    readFile: delegate("readFile"),
    stat: delegate("stat"),
    lstat: delegate("lstat"),
    access: delegate("access"),
    realpath: delegate("realpath"),
    readlink: delegate("readlink"),
    open: fdOpen,
    write: fdWrite,
    read: fdRead,
    opendir: delegate("opendir"),
    readdir: delegate("readdir"),

    writeFile: delegate("writeFile"),
    appendFile: delegate("appendFile"),
    mkdir: delegate("mkdir"),
    rmdir: delegate("rmdir"),
    rm: delegate("rm"),
    unlink: delegate("unlink"),
    rename: delegate("rename"),
    copyFile: delegate("copyFile"),
    chmod: delegate("chmod"),
    utimes: delegate("utimes"),
    symlink: delegate("symlink"),
    link: delegate("link"),

    close: (fd, ...args) => {
        fdTable.delete(fd);
        const cb = lastCallback(args);
        if (cb !== undefined) setTimeout(() => cb(null), 0);
    },
    // no real file descriptors in the browser fs; flush ops are no-op success.
    // util/fs.ts wraps fs.fsync via genFSWrapperAsync, so it must exist (an
    // undefined function there throws "func is not a function").
    fsync: (...args) => {
        const cb = lastCallback(args);
        if (cb !== undefined) {
            setTimeout(() => cb(null), 0);
            return undefined;
        }
        return Promise.resolve();
    },
    fdatasync: (...args) => {
        const cb = lastCallback(args);
        if (cb !== undefined) {
            setTimeout(() => cb(null), 0);
            return undefined;
        }
        return Promise.resolve();
    },
    fsyncSync: () => undefined,
    fdatasyncSync: () => undefined,
    exists: (filePath, cb) => {
        fsPromises
            .stat(filePath)
            .then(() => cb(true))
            .catch(() => cb(false));
    },

    // sync variants: unsupported (FSA is async-only), except files preloaded
    // into the sync snapshot at boot (e.g. the game-image cache JSON, which
    // the renderer reads with a synchronous readFileSync)
    existsSync: (filePath) => core.readSyncSnapshot(filePath) !== undefined,
    readFileSync: (filePath, options) => {
        const snap = core.readSyncSnapshot(filePath);
        if (snap === undefined) {
            throw enoent(filePath);
        }
        const encoding =
            typeof options === "string" ? options : options?.encoding;
        return encoding
            ? Buffer.from(snap).toString(encoding)
            : Buffer.from(snap);
    },
    readdirSync: () => [],
    statSync: (filePath) => {
        throw enoent(filePath);
    },
    lstatSync: (filePath) => {
        throw enoent(filePath);
    },
    accessSync: (filePath) => {
        throw enoent(filePath);
    },
    realpathSync: (filePath) => String(filePath),
    mkdirSync: () => undefined,
    writeFileSync: () => undefined,
    appendFileSync: () => undefined,
    unlinkSync: () => undefined,
    rmdirSync: () => undefined,
    renameSync: () => undefined,
    copyFileSync: () => undefined,
    chmodSync: () => undefined,
    closeSync: () => undefined,
    openSync: (filePath) => {
        throw enoent(filePath);
    },

    watch: () => ({ close: () => undefined, on: () => undefined }),
    watchFile: () => undefined,
    unwatchFile: () => undefined,

    createReadStream: (filePath) => new ReadStreamShim(filePath),
    createWriteStream: () => new WriteStreamShim(),
};

fs.realpathSync.native = fs.realpathSync;
fs.realpath.native = fs.realpath;
fs.promises = fsPromises;
fs.gracefulify = () => undefined;

module.exports = fs;
